import type { ReactNode } from "react";

import clsx from "clsx";
import { useId } from "react";

import { CheckIcon } from "../Icons";
import { Typo } from "../Typo";

export type CheckboxV2Props = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  id?: string;
  name?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  "data-testid"?: string;
};

export const CheckboxV2 = ({
  checked,
  onChange,
  label,
  id,
  name,
  disabled = false,
  error = false,
  errorMessage,
  className,
  "data-testid": testId,
}: CheckboxV2Props) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const showErrorMessage = error && !!errorMessage;

  return (
    <div className={clsx("flex w-full flex-col gap-2", className)}>
      <div className="flex items-start gap-2">
        <label
          htmlFor={inputId}
          className={clsx(
            "flex min-w-0 flex-1 items-start gap-2",
            disabled ? "cursor-not-allowed" : "cursor-pointer",
          )}
        >
          <span className="relative mt-px flex shrink-0">
            <input
              id={inputId}
              name={name}
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={(event) => onChange(event.target.checked)}
              aria-invalid={error || undefined}
              aria-describedby={showErrorMessage ? errorId : undefined}
              data-testid={testId}
              className="peer sr-only"
            />
            <span
              aria-hidden="true"
              className={clsx(
                "box-border flex items-center justify-center rounded border peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-stroke-focused",
                checked
                  ? "border-product-button-bg-selected-default bg-product-button-bg-selected-default"
                  : error
                    ? "bg-surface-primary"
                    : "bg-product-form-bg-default",
                // Figma (IB registration, node 17443:80175): the error box is white with a 2px error border. Figma draws it 24px, but the box stays 20px in every state until the designer confirms. Checked + error isn't designed, so it only inherits the size.
                "h-5 w-5",
                error && "border-2",
                !checked &&
                  (error
                    ? "border-stroke-error"
                    : "border-product-form-border-default"),
                disabled && "opacity-50",
              )}
            >
              {checked && (
                <CheckIcon className="h-3 w-3 text-text-inverse-primary" />
              )}
            </span>
          </span>
          <Typo
            element="span"
            // Figma: the error-state label is Body SM regular, not Label SM semibold.
            type={error ? "bodySM" : "labelSM"}
            textColor={disabled ? "text-text-disabled" : "text-text-primary"}
            className="min-w-0 flex-1"
          >
            {label}
          </Typo>
        </label>
      </div>
      {showErrorMessage && (
        <div
          id={errorId}
          data-testid={testId ? `${testId}_error_message` : undefined}
        >
          {/* Block element on purpose: an inline span inside the wrapper div takes the inherited 24px line height instead of the 18px bodyXS line. */}
          <Typo element="p" type="bodyXS" textColor="text-text-error">
            {errorMessage}
          </Typo>
        </div>
      )}
    </div>
  );
};
